import React, { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as tf from '@tensorflow/tfjs';
import { Hands } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';
import TypeWriter from '../components/TypeWriter';
import './MorseCode.css';

const MORSE_LABELS = ['Dot', 'Dash', 'BlankSpace','BackSpace', 'Next'];
const MORSE_SYMBOLS = { Dot: '.', Dash: '-', BlankSpace: ' ' };
const MIN_STABLE_FRAMES = 8;
const CONFIDENCE_THRESHOLD = 0.6;

const MorseCode = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null); // Add canvas ref
  const webcamCardRef = useRef(null);
  const outputCardRef = useRef(null);
  const [isDetectionActive, setIsDetectionActive] = useState(false);
  const [detectedElement, setDetectedElement] = useState('');
  const [morseText, setMorseText] = useState('');
  const [recentPredictions, setRecentPredictions] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [currentCharacter, setCurrentCharacter] = useState('');
  const [gestureStableCount, setGestureStableCount] = useState(0);
  const [lastGesture, setLastGesture] = useState('');
  const [nextDetected, setNextDetected] = useState(false);
  const modelRef = useRef(null);
  const intervalRef = useRef(null);
  const cameraRef = useRef(null);
  const [handResults, setHandResults] = useState(null);
  const location = useLocation();

  // Helper to read CSS custom properties at runtime for canvas colors
  const getThemeColors = () => {
    try {
      const s = getComputedStyle(document.documentElement);
      return {
        primary: s.getPropertyValue('--primary').trim() || 'rgb(26,115,232)',
        secondary: s.getPropertyValue('--secondary').trim() || 'rgb(0,188,212)',
        highlight: s.getPropertyValue('--highlight').trim() || 'rgb(255,193,7)',
        error: s.getPropertyValue('--error').trim() || 'rgb(255,71,87)',
        success: s.getPropertyValue('--success').trim() || 'rgb(40,167,69)',
        cardBg: s.getPropertyValue('--card-bg').trim() || 'rgb(255,255,255)',
        textDark: s.getPropertyValue('--text-dark').trim() || 'rgb(33,33,33)',
        primaryRgb: s.getPropertyValue('--primary-rgb').trim() || '26,115,232',
        secondaryRgb: s.getPropertyValue('--secondary-rgb').trim() || '0,188,212',
        successRgb: s.getPropertyValue('--success-rgb').trim() || '40,167,69',
        errorRgb: s.getPropertyValue('--error-rgb').trim() || '255,71,87'
      };
    } catch (e) {
      return {
        primary: 'rgb(26,115,232)',
        secondary: 'rgb(0,188,212)',
        highlight: 'rgb(255,193,7)',
        error: 'rgb(255,71,87)',
        success: 'rgb(40,167,69)',
        cardBg: 'rgb(255,255,255)',
        textDark: 'rgb(33,33,33)',
        primaryRgb: '26,115,232',
        secondaryRgb: '0,188,212',
        successRgb: '40,167,69',
        errorRgb: '255,71,87'
      };
    }
  };

  useEffect(() => {
    tf.loadLayersModel('/tfjs_model/hand_gesture/model.json').then(model => {
      modelRef.current = model;
    });
  }, []);

  // Unified start/stop detection logic
  const handleDetectionToggle = () => {
    if (!isDetectionActive) {
      // Start webcam and detection
      setError('');
      setSuccess('Detection started! Use OK gesture for dots (.) and swag gesture for dashes (-).');
      setIsDetectionActive(true);
      startDetection();
    } else {
      // Stop webcam and detection
      setIsDetectionActive(false);
      setSuccess('Detection stopped');
      stopDetection();
    }
  };

  // Start webcam and detection
  const startDetection = () => {
    let localGestureStableCount = 0;
    let localLastGesture = '';
    let localCurrentCharacter = '';
    let localNextDetected = false;

    const hands = new Hands({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`,
    });
    hands.setOptions({ maxNumHands: 2, modelComplexity: 1 });
    hands.onResults(onResults);

    if (videoRef.current) {
      const camera = new Camera(videoRef.current, {
        onFrame: async () => {
          await hands.send({ image: videoRef.current });
        },
        width: 640,
        height: 480,
      });
      camera.start();
      cameraRef.current = camera;
      intervalRef.current = camera;
    }

    function onResults(results) {
      let element = undefined;
      if (
        results.multiHandLandmarks &&
        results.multiHandLandmarks.length > 0 &&
        modelRef.current
      ) {
        // Preprocessing: 21 landmarks, x and y, normalized as in Python
        const handLandmarks = results.multiHandLandmarks[0];
        const xs = handLandmarks.map(pt => pt.x);
        const ys = handLandmarks.map(pt => pt.y);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const landmarks = handLandmarks.flatMap(pt => [pt.x - minX, pt.y - minY]);
        // Predict Morse element
        const input = tf.tensor([landmarks]);
        const predictionTensor = modelRef.current.predict(input);
        const predIdx = predictionTensor.argMax(-1).dataSync()[0];
        const conf = predictionTensor.max(-1).dataSync()[0];
        element = MORSE_LABELS[predIdx];
        setDetectedElement(element);
        setConfidence(conf);
        setSuccess(`Detected: ${element} (Confidence: ${(conf * 100).toFixed(1)}%)`);
        if (conf > CONFIDENCE_THRESHOLD) {
          // Gesture stability logic
          if (element === localLastGesture) {
            localGestureStableCount++;
          } else {
            localGestureStableCount = 0;
            localLastGesture = element;
          }
          setGestureStableCount(localGestureStableCount);
          setLastGesture(localLastGesture);

          if (localGestureStableCount >= MIN_STABLE_FRAMES) {
            if (element === 'Next') {
              if (!localNextDetected) {
                if (localCurrentCharacter === 'BackSpace') {
                  setMorseText(prev => prev.slice(0, -1));
                } else if (localCurrentCharacter && localCurrentCharacter !== 'Next' && localCurrentCharacter !== 'BackSpace') {
                  setMorseText(prev => prev + (MORSE_SYMBOLS[localCurrentCharacter] || ''));
                }
                localNextDetected = true;
                setNextDetected(true);
              }
            } else {
              localCurrentCharacter = element;
              setCurrentCharacter(element);
              localNextDetected = false;
              setNextDetected(false);
            }
            // addToRecentPredictions(element); // Removed as per edit hint
          }
        } else {
          localGestureStableCount = 0;
          setGestureStableCount(0);
        }
        tf.dispose([input, predictionTensor]);
      } else {
        setDetectedElement('');
        setConfidence(0);
        setGestureStableCount(0);
        setCurrentCharacter('');
        setLastGesture('');
        setNextDetected(false);
      }
      // Draw skeleton and bounding box
      try {
        if (canvasRef.current && videoRef.current && videoRef.current.videoWidth > 0) {
          const canvas = canvasRef.current;
          if (!canvas || !canvas.getContext) return;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          
          // Clear canvas safely
          try {
            ctx.clearRect(0, 0, canvas.width || 640, canvas.height || 480);
          } catch (e) {
            console.log('Error clearing canvas:', e);
          }
          
          // Set canvas size to match video
          try {
            if (videoRef.current.videoWidth && videoRef.current.videoHeight) {
              canvas.width = videoRef.current.videoWidth;
              canvas.height = videoRef.current.videoHeight;
            }
          } catch (error) {
            console.log('Error setting canvas dimensions:', error);
            return;
          }
          
          if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
            const showPrediction = results.multiHandLandmarks.length === 1;
            results.multiHandLandmarks.forEach((landmarks, idx) => {
              if (!landmarks) return;
              
              const safeWidth = canvas.width || 640;
              const safeHeight = canvas.height || 480;
              
              try {
                drawHandSkeleton(ctx, landmarks, safeWidth, safeHeight);
                let label = '';
                if (showPrediction && idx === 0) {
                  if (typeof element !== 'undefined') {
                    if (element === 'Dot') label = 'Dot';
                    else if (element === 'Dash') label = 'Dash';
                    else if (element === 'BlankSpace') label = 'BlankSpace';
                    else if (element === 'BackSpace') label = 'BackSpace';
                    else if (element === 'Next') label = 'Next';
                  }
                }
                drawBoundingBox(ctx, landmarks, safeWidth, safeHeight, label);
              } catch (e) {
                console.log('Error drawing landmarks:', e);
              }
            });
          }
        }
      } catch (error) {
        console.log('Error in drawing function:', error);
      }
      setHandResults(results);
    }
  };

  // Stop webcam and detection
  const stopDetection = () => {
    try {
      // Stop camera stream
      if (cameraRef.current) {
        if (cameraRef.current.stop) {
          cameraRef.current.stop();
        }
        cameraRef.current = null;
      }
      
      // Clear interval
      if (intervalRef.current) {
        if (intervalRef.current.stop) {
          intervalRef.current.stop();
        }
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Stop video tracks
      if (videoRef.current && videoRef.current.srcObject) {
        try {
          const tracks = videoRef.current.srcObject.getTracks();
          tracks.forEach(track => {
            try {
              track.stop();
            } catch (e) {
              console.log('Error stopping track:', e);
            }
          });
          videoRef.current.srcObject = null;
        } catch (e) {
          console.log('Error stopping video stream:', e);
        }
      }
      
      // Clear canvas
      if (canvasRef.current) {
        try {
          const ctx = canvasRef.current.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvasRef.current.width || 640, canvasRef.current.height || 480);
          }
        } catch (e) {
          console.log('Error clearing canvas:', e);
        }
      }
      
      setDetectedElement('');
      setConfidence(0);
      setGestureStableCount(0);
      setCurrentCharacter('');
      setLastGesture('');
      setNextDetected(false);
      setIsDetectionActive(false);
      
    } catch (error) {
      console.log('Error in stopDetection:', error);
    }
  };

  // Remove addToRecentPredictions and gesture label tracking
  // const addToRecentPredictions = (element) => {
  //   const newPrediction = {
  //     element,
  //     timestamp: new Date().toLocaleTimeString(),
  //     date: new Date().toLocaleDateString()
  //   };
  //   setRecentPredictions(prev => {
  //     const updated = [newPrediction, ...prev.slice(0, 4)];
  //     return updated;
  //   });
  // };

  const clearAll = () => {
    setDetectedElement('');
    setMorseText('');
    setCurrentCharacter('');
    setGestureStableCount(0);
    setLastGesture('');
    setNextDetected(false);
    setRecentPredictions([]);
    setError('');
    setSuccess('');
    setConfidence(0);
  };

  // Replace morseText tracking useEffect with symbol tracking
  useEffect(() => {
    // Only update if morseText actually changed
    setRecentPredictions(prev => {
      // If cleared, clear predictions
      if (!morseText) return [];
      // If a symbol was added
      if (prev.length === 0 || morseText.length > prev.map(p => p.symbol).join('').length) {
        const newSymbol = morseText[morseText.length - 1];
        if (!['.', '-', ' '].includes(newSymbol)) return prev;
        return [{ symbol: newSymbol, timestamp: new Date().toLocaleTimeString() }, ...prev].slice(0, 5);
      }
      // If a symbol was removed (backspace)
      if (morseText.length < prev.map(p => p.symbol).join('').length) {
        return prev.slice(1);
      }
      return prev;
    });
    // eslint-disable-next-line
  }, [morseText]);

  useEffect(() => {
    return () => {
      stopDetection();
    };
    // eslint-disable-next-line
  }, []);

  // Enhanced cleanup on unmount
  useEffect(() => {
    return () => {
      // Ensure all detection is fully stopped
      if (intervalRef.current) {
        if (intervalRef.current.stop) intervalRef.current.stop();
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Stop webcam and clean up streams
      stopDetection();
      
      // Stop any active webcam streams globally
      const videoElements = document.querySelectorAll('video');
      videoElements.forEach(videoElement => {
        if (videoElement && videoElement.srcObject) {
          const tracks = videoElement.srcObject.getTracks();
          tracks.forEach(track => {
            try {
              track.stop();
            } catch (e) {
              console.log('Error stopping track:', e);
            }
          });
          videoElement.srcObject = null;
        }
      });
      
      // Clear any remaining intervals
      const maxIntervalId = setInterval(() => {}, 0);
      for (let i = 0; i < maxIntervalId; i++) {
        try {
          clearInterval(i);
        } catch (e) {
          // Ignore errors from clearing non-existent intervals
        }
      }
      
      // Clear canvas references
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          try {
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          } catch (e) {
            console.log('Error clearing canvas:', e);
          }
        }
      }
    };
  }, []);

  // Cleanup on route change
    useEffect(() => {
      const currentPath = location.pathname;
      return () => {
        if (currentPath !== '/morse-code') {
          try {
            // Stop detection when navigating away
            stopDetection();
            
            // Force cleanup of all video elements
            const videoElements = document.querySelectorAll('video');
            videoElements.forEach(videoElement => {
              if (videoElement && videoElement.srcObject) {
                const tracks = videoElement.srcObject.getTracks();
                tracks.forEach(track => {
                  try {
                    track.stop();
                  } catch (e) {
                    console.log('Error stopping track on route change:', e);
                  }
                });
                videoElement.srcObject = null;
              }
            });
            
          } catch (error) {
            console.log('Error during route cleanup:', error);
          }
        }
      };
    }, [location.pathname, isDetectionActive]);

  const copyToClipboard = () => {
    if (morseText) {
      navigator.clipboard.writeText(morseText);
      setSuccess('Morse code copied to clipboard!');
    }
  };

  const clearRecentPredictions = () => {
    setRecentPredictions([]);
    setSuccess('Recent predictions cleared!');
  };

  // Force equal height for both cards on desktop
  useEffect(() => {
    function setEqualCardHeights() {
      if (window.innerWidth > 900 && webcamCardRef.current && outputCardRef.current) {
        const webcamHeight = webcamCardRef.current.offsetHeight;
        outputCardRef.current.style.height = webcamHeight + 'px';
      } else if (outputCardRef.current) {
        outputCardRef.current.style.height = 'auto';
      }
    }
    setEqualCardHeights();
    window.addEventListener('resize', setEqualCardHeights);
    return () => window.removeEventListener('resize', setEqualCardHeights);
  });

  // Add a helper function to get the label for a symbol
  const getSymbolLabel = (symbol) => {
    if (symbol === '.') return 'Dot';
    if (symbol === '-') return 'Dash';
    if (symbol === ' ') return 'BlankSpace';
    return symbol;
  };

  // Update drawHandSkeleton for bright neon colors
  function drawHandSkeleton(ctx, landmarks, w, h) {
    const theme = getThemeColors();
    // Use theme colors for skeleton strokes
    const fingerDefs = [
      { indices: [0,1,2,3,4], color: theme.primary },
      { indices: [0,5,6,7,8], color: theme.secondary },
      { indices: [5,9,10,11,12], color: theme.highlight },
      { indices: [9,13,14,15,16], color: theme.textDark },
      { indices: [13,17,18,19,20], color: theme.secondary },
      { indices: [0,17], color: theme.secondary }
    ];
    // Draw each finger
    fingerDefs.forEach(finger => {
      ctx.strokeStyle = finger.color;
      ctx.lineWidth = 4;
      for (let i = 0; i < finger.indices.length - 1; i++) {
        const start = finger.indices[i];
        const end = finger.indices[i + 1];
        ctx.beginPath();
        ctx.moveTo(landmarks[start].x * w, landmarks[start].y * h);
        ctx.lineTo(landmarks[end].x * w, landmarks[end].y * h);
        ctx.stroke();
      }
    });
    // Draw landmarks (card background with neon border)
    landmarks.forEach(pt => {
      ctx.beginPath();
      ctx.arc(pt.x * w, pt.y * h, 7, 0, 2 * Math.PI);
      ctx.fillStyle = theme.cardBg || '#FFFFFF';
      ctx.shadowColor = `rgba(${theme.secondaryRgb || '0,188,212'}, 0.9)`;
      ctx.shadowBlur = 10;
      ctx.fill();
      ctx.shadowBlur = 0;
    });
  }

  // Update drawBoundingBox for real-time label
  function drawBoundingBox(ctx, landmarks, w, h, label) {
    const xs = landmarks.map(pt => pt.x * w);
    const ys = landmarks.map(pt => pt.y * h);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const theme = getThemeColors();
    ctx.strokeStyle = theme.error || '#FF0000';
    ctx.lineWidth = 5;
    ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
    // Draw label at top center in real time
    if (label) {
      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = theme.success || '#00FF00';
      ctx.textAlign = 'center';
      ctx.strokeStyle = theme.textDark || '#000';
      ctx.lineWidth = 6;
      ctx.strokeText(label, (minX + maxX) / 2, minY - 16 < 36 ? minY + 36 : minY - 16);
      ctx.fillText(label, (minX + maxX) / 2, minY - 16 < 36 ? minY + 36 : minY - 16);
    }
  }

  return (
    <div className="morse-code-page">
      <div className="container">
        {/* Header */}
        <div className="morse-code-header card" data-aos="fade-up" data-aos-duration="900">
          <h1>
            <TypeWriter 
              text="📡 Morse Code Recognition" 
              speed={80} 
              cursorColor="white"
            />
          </h1>
          <p>Use your webcam to recognize Morse code gestures in real-time</p>
        </div>

        {/* Webcam and Output Side by Side */}
        <div className="morse-flex-row">
          {/* Webcam Section */}
          <div className="webcam-section card" ref={webcamCardRef} data-aos="zoom-in" data-aos-delay="100" data-aos-duration="800">
            <div className="webcam-header">
              <h2 className="morse-code-section-heading">Morse Code Recognition</h2>
              <div className="webcam-controls">
                <button
                  className={`realtime-btn${isDetectionActive ? ' stop' : ''}`}
                  onClick={handleDetectionToggle}
                >
                  {isDetectionActive ? '⏹️ Stop Real-time Detection' : '🔄 Start Detection'}
                </button>
              </div>
            </div>
            <div className="webcam-container" data-aos="fade-up" data-aos-delay="150" data-aos-duration="700" style={{position: 'relative'}}>
              {handResults && handResults.multiHandLandmarks && handResults.multiHandLandmarks.length > 1 && (
                <div style={{
                  position: 'absolute',
                  top: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(var(--error-rgb),0.85)',
                  color: 'white',
                  padding: '8px 18px',
                  borderRadius: 12,
                  fontWeight: 700,
                  zIndex: 10,
                  fontSize: 20
                }}>
                  Multiple hands detected
                </div>
              )}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                width={640}
                height={480}
                className="webcam-video"
                style={{ borderRadius: '16px', background: 'var(--color-bg)' }}
              />
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 2
                }}
              />
            </div>
            {(detectedElement || isDetectionActive) && (
              <div className="morse-result" data-aos="fade-in" data-aos-delay="200">
                {detectedElement ? (
                  <p>Detected Element: <strong>{detectedElement}</strong> (Confidence: {(confidence * 100).toFixed(1)}%)</p>
                ) : (
                  <p>Real-time detection active - Show your hands to see Morse elements</p>
                )}
              </div>
            )}
          </div>

          {/* Morse Code Display Section */}
          <div className="morse-display-section card" ref={outputCardRef} data-aos="zoom-in" data-aos-delay="200" data-aos-duration="800">
            <h2 className="morse-code-section-heading">Morse Code Output</h2>
            <div className="morse-container" style={{flex: 1}}>
              <textarea
                value={morseText}
                readOnly
                className="morse-code-output"
                placeholder="Morse code will appear here..."
                rows={10}
              />
              <div className="current-morse-buffer">
                <strong>Current Character:</strong> {currentCharacter}
                {currentCharacter && currentCharacter !== 'Next' && (
                  <span style={{ color: currentCharacter === 'BackSpace' ? 'var(--error)' : 'var(--success)', marginLeft: 8 }}>
                    {currentCharacter === 'BackSpace' ? " (Show 'Next' to delete)" : " (Show 'Next' to add)"}
                  </span>
                )}
              </div>
              <div className="morse-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button 
                  className="btn btn-primary copy-btn"
                  onClick={copyToClipboard}
                  disabled={!morseText}
                >
                  📋 Copy Morse
                </button>
                <button 
                  className="btn btn-secondary clear-all-btn"
                  onClick={clearAll}
                  disabled={!morseText && !currentCharacter && !gestureStableCount}
                >
                  🗑️ Clear All
                </button>
              </div>
            </div>
              <div className="debug-status" style={{ marginTop: 10, fontSize: '0.95em', color: 'var(--text-dark)', background: 'linear-gradient(135deg, var(--bg-light), var(--card-bg))', borderRadius: 8, padding: 8 }}>
              <div><strong>Status:</strong> {success || error || 'Ready'}</div>
              <div><strong>Stable:</strong> {gestureStableCount}/{MIN_STABLE_FRAMES} | <strong>Confidence:</strong> {(confidence * 100).toFixed(1)}%</div>
              <div><strong>Last Gesture:</strong> {lastGesture}</div>
            </div>
          </div>
        </div>

        {/* Recent Predictions */}
        <div className="recent-predictions-section card" data-aos="zoom-in" data-aos-delay="250" data-aos-duration="800">
          <h2 className="morse-code-section-heading">📊 Recent Predictions</h2>
          <div className="predictions-container">
            {recentPredictions.length > 0 ? (
              <div className="predictions-list">
                {recentPredictions.map((prediction, index) => (
                  <div key={index} className="prediction-item" data-aos="fade-up" data-aos-delay={300 + index * 50}>
                    <div className="prediction-element">
                      <span className="element-symbol">{prediction.symbol}</span>
                      <span style={{ marginLeft: 8, fontWeight: 500, color: 'var(--primary)' }}>{getSymbolLabel(prediction.symbol)}</span>
                    </div>
                    <div className="prediction-details">
                      <span className="prediction-time">{prediction.timestamp}</span>
                    </div>
                    <div className="prediction-number">#{index + 1}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-predictions" data-aos="fade-up" data-aos-delay="300">
                <p>No predictions yet. Start detecting Morse code to see recent predictions!</p>
              </div>
            )}
            {recentPredictions.length > 0 && (
              <div className="predictions-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={clearRecentPredictions}
                >
                  🗑️ Clear Predictions
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="instructions-section card" data-aos="fade-up" data-aos-delay="300" data-aos-duration="900">
          <h2 className="morse-code-section-heading">How to Use</h2>
          <div className="instructions-grid">
            <div className="instruction-item" data-aos="fade-right" data-aos-delay="350">
              <div className="instruction-icon">📹</div>
              <h4>Start Webcam</h4>
              <p>Click "Start Webcam" to enable camera access</p>
            </div>
            <div className="instruction-item" data-aos="fade-up" data-aos-delay="400">
              <div className="instruction-icon">👍</div>
              <h4>Make Gestures</h4>
              <p>Use OK gesture for dots (.) and swag gesture for dashes (-)</p>
            </div>
            <div className="instruction-item" data-aos="fade-up" data-aos-delay="450">
              <div className="instruction-icon">📡</div>
              <h4>Detect Morse</h4>
              <p>Click "Detect Morse" to recognize hand gestures</p>
            </div>
            <div className="instruction-item" data-aos="fade-left" data-aos-delay="500">
              <div className="instruction-icon">📋</div>
              <h4>View Results</h4>
              <p>See detected Morse code in the results section</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="message error fade-in" data-aos="fade-in" data-aos-delay="600">
            {error}
          </div>
        )}
        
        {success && (
          <div className="message success fade-in" data-aos="fade-in" data-aos-delay="650">
            {success}
          </div>
        )}
      </div>
    </div>
  );
};

export default MorseCode;